import React, { useState, useEffect } from 'react';
import { 
  Settings, Cpu, HardDrive, MemoryStick, Activity, 
  Database, Globe, TestTube, CheckCircle, XCircle,
  RefreshCw, AlertTriangle, TrendingUp, Server,
  Monitor, Wifi, Clock, Download, Upload, Zap,
  LineChart, Play, Pause, FileText, BarChart3,  Terminal,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  File,
  Search
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';
export const SystemManagement = () => {
  const [systemStats, setSystemStats] = useState({
    cpu: { usage: 0, cores: 0, frequency: 0 },
    memory: { used: 0, total: 0, available: 0, percent: 0 },
    disk: { used: 0, total: 0, free: 0, percent: 0 },
    network: { sent: 0, received: 0 },
    uptime: 0,
    load_average: [0, 0, 0]
  });
  
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { colors } = useTheme();

  const [logFiles, setLogFiles] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState(null);
  const [logContent, setLogContent] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logStats, setLogStats] = useState(null);
  const [logSearchTerm, setLogSearchTerm] = useState('');
  const [logSearchResults, setLogSearchResults] = useState([]);
  const [logViewMode, setLogViewMode] = useState('recent'); // 'recent' or 'search'
  const [logAutoRefresh, setLogAutoRefresh] = useState(false);
  const [expandedDirectories, setExpandedDirectories] = useState(new Set(['Application Logs']));

  // API Performance Monitor States
  const [apiMonitorActive, setApiMonitorActive] = useState(false);
  const [apiPingInterval, setApiPingInterval] = useState(5); // seconds
  const [apiPerformanceData, setApiPerformanceData] = useState([]);
  const [latestApiResponse, setLatestApiResponse] = useState(null);
  const [apiStats, setApiStats] = useState({
    totalPings: 0,
    avgResponseTime: 0,
    minResponseTime: null,
    maxResponseTime: null,
    successRate: 100,
    lastPingTime: null
  });

  const loadLogFiles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/logs/list`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogFiles(data.logs);
      }
    } catch (error) {
      console.error('Failed to load log files:', error);
    }
  };

  const loadLogStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/logs/stats`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load log stats:', error);
    }
  };

  const loadLogContent = async (filePath, lines = 100) => {
    setLogLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/logs/content?file=${encodeURIComponent(filePath)}&lines=${lines}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogContent(data.content);
      }
    } catch (error) {
      console.error('Failed to load log content:', error);
    } finally {
      setLogLoading(false);
    }
  };

  const searchLogContent = async () => {
    if (!selectedLogFile || !logSearchTerm.trim()) return;
    
    setLogLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/logs/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          file: selectedLogFile.path,
          search_term: logSearchTerm,
          case_sensitive: false,
          max_results: 100
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogSearchResults(data.matches);
        setLogViewMode('search');
      }
    } catch (error) {
      console.error('Failed to search logs:', error);
    } finally {
      setLogLoading(false);
    }
  };

  const selectLogFile = (logFile) => {
    setSelectedLogFile(logFile);
    setLogViewMode('recent');
    setLogSearchResults([]);
    loadLogContent(logFile.path);
  };

  const toggleDirectory = (directoryName) => {
    setExpandedDirectories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(directoryName)) {
        newSet.delete(directoryName);
      } else {
        newSet.add(directoryName);
      }
      return newSet;
    });
  };

  const downloadLogFile = (filePath) => {
    const url = `${API_URL}/api/admin/logs/download?file=${encodeURIComponent(filePath)}`;
    window.open(url, '_blank');
  };

  // Group log files by directory
  const groupedLogFiles = logFiles.reduce((acc, file) => {
    if (!acc[file.directory]) {
      acc[file.directory] = [];
    }
    acc[file.directory].push(file);
    return acc;
  }, {});

  const getLogTypeColor = (type) => {
    switch (type) {
      case 'app': return '#ef4444';
      case 'inetpub': return '#10b981';
      case 'system': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  // Mock API_URL for demo - replace with your actual API_URL import
  useEffect(() => {
    loadSystemStats();
    loadLogFiles();      // Add this line
    loadLogStats();      // Add this line
    loadApiPerformanceHistory();
    
    if (autoRefresh) {
      const interval = setInterval(loadSystemStats, 50000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  useEffect(() => {
    if (logAutoRefresh && selectedLogFile) {
      const interval = setInterval(() => loadLogContent(selectedLogFile.path), 5000);
      return () => clearInterval(interval);
    }
  }, [logAutoRefresh, selectedLogFile]);
  // API Monitor interval
  useEffect(() => {
    let interval;
    if (apiMonitorActive) {
      pingProductsApi(); // Initial ping
      interval = setInterval(pingProductsApi, apiPingInterval * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [apiMonitorActive, apiPingInterval]);

  

  const loadSystemStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system/stats`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSystemStats(data.stats);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to load system stats:', error);
      // Mock data for demo
      setSystemStats({
        cpu: { usage: Math.random() * 100, cores: 8, frequency: 2400 },
        memory: { 
          used: 8.2, 
          total: 16, 
          available: 7.8, 
          percent: Math.random() * 80 + 10 
        },
        disk: { 
          used: 245, 
          total: 500, 
          free: 255, 
          percent: Math.random() * 60 + 20 
        },
        network: { sent: 1024, received: 2048 },
        uptime: 86400 * 7,
        load_average: [0.8, 1.2, 0.9]
      });
      setLastUpdated(new Date());
    }
  };

  const pingProductsApi = async () => {
    const startTime = performance.now();
    const timestamp = new Date();
    
    try {
      // Build the API request with all parameters
      const params = new URLSearchParams({
        page: '1',
        limit: '50', // Static 50 as requested
        sort_by: 'rating'
      });

      const response = await fetch(`${API_URL}/api/get-products-pagination?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      const responseData = response.ok ? await response.json() : null;
      
      const pingResult = {
        timestamp: timestamp.toISOString(),
        responseTime: responseTime / 1000,
        status: response.status,
        success: response.ok,
        productCount: responseData?.products?.length || 0,
        totalProducts: responseData?.pagination?.total_products || 0
      };

      // Update latest response
      setLatestApiResponse(pingResult);

      // Save to performance history
      await saveApiPerformanceData(pingResult);

      // Update performance data for chart
      setApiPerformanceData(prev => {
        const newData = [...prev, {
          time: timestamp.toLocaleTimeString(),
          responseTime: parseFloat((responseTime / 1000).toFixed(3)), 
          status: response.status
        }].slice(-50); // Keep last 50 data points
        
        // Update stats
        updateApiStats(newData);
        
        return newData;
      });

    } catch (error) {
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      const pingResult = {
        timestamp: timestamp.toISOString(),
        responseTime: responseTime / 1000,
        status: 0,
        success: false,
        error: error.message
      };

      setLatestApiResponse(pingResult);
      await saveApiPerformanceData(pingResult);

      setApiPerformanceData(prev => {
        const newData = [...prev, {
          time: timestamp.toLocaleTimeString(),
          responseTime: parseFloat((responseTime / 1000).toFixed(3)),
          status: 0
        }].slice(-50);
        
        updateApiStats(newData);
        return newData;
      });
    }
  };

  const updateApiStats = (data) => {
    if (data.length === 0) return;

    const responseTimes = data.map(d => d.responseTime);
    const successfulPings = data.filter(d => d.status === 200).length;

    setApiStats({
      totalPings: data.length,
      avgResponseTime: responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length,
      minResponseTime: Math.min(...responseTimes),
      maxResponseTime: Math.max(...responseTimes),
      successRate: (successfulPings / data.length) * 100,
      lastPingTime: new Date()
    });
  };

  const saveApiPerformanceData = async (data) => {
    try {
      // In production, this would be an API call to save to backend
      // For now, we'll simulate saving to localStorage
      const history = JSON.parse(localStorage.getItem('apiPerformanceHistory') || '[]');
      history.push(data);
      
      // Keep only last 1000 records
      if (history.length > 1000) {
        history.splice(0, history.length - 1000);
      }
      
      localStorage.setItem('apiPerformanceHistory', JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save performance data:', error);
    }
  };

  const loadApiPerformanceHistory = async () => {
    try {
      // In production, this would load from backend
      const history = JSON.parse(localStorage.getItem('apiPerformanceHistory') || '[]');
      
      // Convert to chart format (last 50 entries)
      const chartData = history.slice(-50).map(item => ({
        time: new Date(item.timestamp).toLocaleTimeString(),
        responseTime: parseFloat(item.responseTime.toFixed(2)),
        status: item.status
      }));
      
      setApiPerformanceData(chartData);
      if (chartData.length > 0) {
        updateApiStats(chartData);
      }
    } catch (error) {
      console.error('Failed to load performance history:', error);
    }
  };

  const exportPerformanceData = () => {
    try {
      const history = JSON.parse(localStorage.getItem('apiPerformanceHistory') || '[]');
      const dataStr = JSON.stringify(history, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `api_performance_${new Date().toISOString()}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  const clearPerformanceData = () => {
      localStorage.removeItem('apiPerformanceHistory');
      setApiPerformanceData([]);
      setApiStats({
        totalPings: 0,
        avgResponseTime: 0,
        minResponseTime: null,
        maxResponseTime: null,
        successRate: 100,
        lastPingTime: null
      });
    
  };

  const runTest = async (testType) => {
    setLoading(true);
    setTestResults(prev => ({ ...prev, [testType]: { status: 'running' } }));

    try {
      const response = await fetch(`${API_URL}/api/admin/system/test/${testType}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      setTestResults(prev => ({
        ...prev,
        [testType]: {
          status: response.ok ? 'success' : 'error',
          message: data.message || data.error,
          duration: data.duration,
          timestamp: new Date()
        }
      }));
    } catch (error) {
      // Mock test results for demo
      const mockResults = {
        'api-get': { 
          status: 'success', 
          message: 'GET /api/health returned 200 OK', 
          duration: Math.random() * 100 + 50 
        },
        'api-post': { 
          status: 'success', 
          message: 'POST /api/test completed successfully', 
          duration: Math.random() * 200 + 100 
        },
        'database': { 
          status: 'success', 
          message: 'Database connection and query successful', 
          duration: Math.random() * 50 + 20 
        },
        'requests': { 
          status: 'success', 
          message: 'External request test completed', 
          duration: Math.random() * 500 + 200 
        }
      };
      
      setTestResults(prev => ({
        ...prev,
        [testType]: {
          ...mockResults[testType],
          timestamp: new Date()
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${mins}m`;
  };

  const getStatusColor = (percentage) => {
    if (percentage < 50) return '#10b981';
    if (percentage < 80) return '#f59e0b';
    return '#ef4444';
  };

  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#333"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#e5e5e5' }}>
            {percentage.toFixed(1)}%
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px' 
      }}>
        <div>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#e5e5e5' 
          }}>
            System Management
          </h2>
          <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
            Monitor system performance and run diagnostic tests
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            color: '#e5e5e5',
            fontSize: '14px'
          }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ accentColor: '#ef4444' }}
            />
            Auto-refresh
          </label>
          
          <button
            onClick={loadSystemStats}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: colors.bgCard,
              border: '1px solid #333',
              borderRadius: '12px',
              color: '#e5e5e5',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <div style={{
          background: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#888',
          fontSize: '14px'
        }}>
          <Clock size={16} />
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      {/* System Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* CPU Usage */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid #333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Cpu size={24} style={{ color: '#ef4444' }} />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#e5e5e5' }}>
              CPU Usage
            </h3>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <CircularProgress 
              percentage={systemStats.cpu.usage} 
              color={getStatusColor(systemStats.cpu.usage)}
            />
            <div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Cores: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.cpu.cores}</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Frequency: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.cpu.frequency} MHz</span>
              </div>
              <div>
                <span style={{ color: '#888', fontSize: '14px' }}>Load Avg: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>
                  {systemStats.load_average.map(l => l.toFixed(2)).join(', ')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Memory Usage */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid #333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <MemoryStick size={24} style={{ color: '#8b5cf6' }} />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#e5e5e5' }}>
              Memory Usage
            </h3>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <CircularProgress 
              percentage={systemStats.memory.percent} 
              color={getStatusColor(systemStats.memory.percent)}
            />
            <div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Used: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.memory.used} GB</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Total: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.memory.total} GB</span>
              </div>
              <div>
                <span style={{ color: '#888', fontSize: '14px' }}>Available: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.memory.available} GB</span>
              </div>
            </div>
          </div>
        </div>

        {/* Disk Usage */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid #333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <HardDrive size={24} style={{ color: '#10b981' }} />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#e5e5e5' }}>
              Disk Usage
            </h3>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <CircularProgress 
              percentage={systemStats.disk.percent} 
              color={getStatusColor(systemStats.disk.percent)}
            />
            <div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Used: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.disk.used} GB</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Total: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.disk.total} GB</span>
              </div>
              <div>
                <span style={{ color: '#888', fontSize: '14px' }}>Free: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>{systemStats.disk.free} GB</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Server size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>System Uptime</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{formatUptime(systemStats.uptime)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Download size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Network In</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{formatBytes(systemStats.network.received)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Upload size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Network Out</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>{formatBytes(systemStats.network.sent)}</div>
        </div>
      </div>

      {/* API Performance Monitor Section */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '20px',
        padding: '24px',
        border: '1px solid #333',
        marginBottom: '32px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BarChart3 size={24} style={{ color: '#10b981' }} />
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#e5e5e5' }}>
              Products API Performance Monitor
            </h3>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={apiPingInterval}
              onChange={(e) => setApiPingInterval(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                background: colors.bgCard,
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value={5}>5 seconds</option>
              <option value={10}>10 seconds</option>
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
            </select>
            
            <button
              onClick={() => setApiMonitorActive(!apiMonitorActive)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: apiMonitorActive 
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {apiMonitorActive ? <Pause size={16} /> : <Play size={16} />}
              {apiMonitorActive ? 'Stop Monitor' : 'Start Monitor'}
            </button>
          </div>
        </div>

        {/* API Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #333'
          }}>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Total Pings</div>
            <div style={{ color: '#e5e5e5', fontSize: '24px', fontWeight: '700' }}>{apiStats.totalPings}</div>
          </div>
          
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #333'
          }}>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Avg Response</div>
            <div style={{ color: '#e5e5e5', fontSize: '24px', fontWeight: '700' }}>
              {apiStats.avgResponseTime.toFixed(3)}s

            </div>
          </div>
          
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #333'
          }}>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Min / Max</div>
            <div style={{ color: '#e5e5e5', fontSize: '24px', fontWeight: '700' }}>
              {apiStats.minResponseTime?.toFixed(3) || '-'} / {apiStats.maxResponseTime?.toFixed(3) || '-'}s

            </div>
          </div>
          
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #333'
          }}>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Success Rate</div>
            <div style={{ 
              color: apiStats.successRate === 100 ? '#10b981' : apiStats.successRate > 90 ? '#f59e0b' : '#ef4444', 
              fontSize: '24px', 
              fontWeight: '700' 
            }}>
              {apiStats.successRate.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Latest Response Details */}
        {latestApiResponse && (
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            border: `1px solid ${latestApiResponse.success ? '#10b981' : '#ef4444'}`
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#e5e5e5' }}>
                Latest API Response
              </h4>
              <span style={{ 
                fontSize: '12px', 
                color: '#888' 
              }}>
                {new Date(latestApiResponse.timestamp).toLocaleString()}
              </span>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <span style={{ color: '#888', fontSize: '12px' }}>Status: </span>
                <span style={{ 
                  color: latestApiResponse.success ? '#10b981' : '#ef4444',
                  fontWeight: '500'
                }}>
                  {latestApiResponse.status || 'Error'}
                </span>
              </div>
              <div>
                <span style={{ color: '#888', fontSize: '12px' }}>Response Time: </span>
                <span style={{ color: '#e5e5e5', fontWeight: '500' }}>
                  {latestApiResponse.responseTime.toFixed(3)}s
                </span>
              </div>
              {latestApiResponse.productCount !== undefined && (
                <div>
                  <span style={{ color: '#888', fontSize: '12px' }}>Products Fetched: </span>
                  <span style={{ color: '#e5e5e5', fontWeight: '500' }}>
                    {latestApiResponse.productCount}
                  </span>
                </div>
              )}
              {latestApiResponse.totalProducts !== undefined && (
                <div>
                  <span style={{ color: '#888', fontSize: '12px' }}>Total Products: </span>
                  <span style={{ color: '#e5e5e5', fontWeight: '500' }}>
                    {latestApiResponse.totalProducts}
                  </span>
                </div>
              )}
              {latestApiResponse.error && (
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ color: '#888', fontSize: '12px' }}>Error: </span>
                  <span style={{ color: '#ef4444', fontSize: '12px' }}>
                    {latestApiResponse.error}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Performance Chart */}
        {apiPerformanceData.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#e5e5e5' 
            }}>
              Response Time History
            </h4>
            <div style={{ height: '300px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={apiPerformanceData}>
                  <defs>
                    <linearGradient id="colorResponseTime" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#888"
                    fontSize={12}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    stroke="#888"
                    fontSize={12}
                    label={{ value: 'Response Time (s)', angle: -90, position: 'insideLeft', style: { fill: '#888' } }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: colors.bgCard, 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#e5e5e5' }}
                    itemStyle={{ color: '#10b981' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="responseTime" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fill="url(#colorResponseTime)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Data Management Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={exportPerformanceData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: colors.bgCard,
              border: '1px solid #333',
              borderRadius: '8px',
              color: '#e5e5e5',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <FileText size={16} />
            Export Data
          </button>
          
          <button
            onClick={clearPerformanceData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: colors.bgCard,
              border: '1px solid #333',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <XCircle size={16} />
            Clear History
          </button>
        </div>
      </div>

      {/* System Tests */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '20px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <TestTube size={24} style={{ color: '#ef4444' }} />
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#e5e5e5' }}>
            System Tests
          </h3>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          {[
            { id: 'api-get', name: 'API GET Test', icon: Globe, description: 'Test GET endpoint health' },
            { id: 'api-post', name: 'API POST Test', icon: Zap, description: 'Test POST endpoint functionality' },
            { id: 'database', name: 'Database Test', icon: Database, description: 'Test database connection and query' },
            { id: 'requests', name: 'External Requests', icon: Wifi, description: 'Test external API connectivity' }
          ].map(test => (
            <div key={test.id} style={{
              background: colors.bgCard,
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #333'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <test.icon size={20} style={{ color: '#ef4444' }} />
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
                  {test.name}
                </h4>
              </div>
              
              <p style={{ 
                margin: '0 0 16px 0', 
                color: '#888', 
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                {test.description}
              </p>

              {testResults[test.id] && (
                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  background: testResults[test.id].status === 'success' ? '#10b98120' : 
                              testResults[test.id].status === 'error' ? '#ef444420' : '#f59e0b20',
                  border: `1px solid ${testResults[test.id].status === 'success' ? '#10b981' : 
                                      testResults[test.id].status === 'error' ? '#ef4444' : '#f59e0b'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    {testResults[test.id].status === 'success' && <CheckCircle size={16} style={{ color: '#10b981' }} />}
                    {testResults[test.id].status === 'error' && <XCircle size={16} style={{ color: '#ef4444' }} />}
                    {testResults[test.id].status === 'running' && <RefreshCw size={16} style={{ color: '#f59e0b', animation: 'spin 1s linear infinite' }} />}
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: testResults[test.id].status === 'success' ? '#10b981' : 
                             testResults[test.id].status === 'error' ? '#ef4444' : '#f59e0b'
                    }}>
                      {testResults[test.id].status === 'running' ? 'Running...' : 
                       testResults[test.id].status === 'success' ? 'Success' : 'Error'}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#e5e5e5', marginBottom: '4px' }}>
                    {testResults[test.id].message}
                  </div>
                  {testResults[test.id].duration && (
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      Duration: {testResults[test.id].duration.toFixed(0)}ms
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => runTest(test.id)}
                disabled={loading && testResults[test.id]?.status === 'running'}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: loading && testResults[test.id]?.status === 'running' 
                    ? '#555' 
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading && testResults[test.id]?.status === 'running' ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading && testResults[test.id]?.status === 'running' ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Running...
                  </>
                ) : (
                  <>
                    <TestTube size={16} />
                    Run Test
                  </>
                )}
              </button>
            </div>
          ))}
          
        </div>
      </div>
      <div style={{
          background: '#1a1a1a',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid #333',
          marginBottom: '32px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Terminal size={24} style={{ color: '#10b981' }} />
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#e5e5e5' }}>
                Log File Viewer
              </h3>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {logStats && (
                <div style={{ 
                  background: colors.bgCard, 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '1px solid #333',
                  fontSize: '12px',
                  color: '#888'
                }}>
                  {logStats.total_files} files • {formatBytes(logStats.total_size)}
                </div>
              )}
              
              <button
                onClick={loadLogFiles}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: colors.bgCard,
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px', height: '600px' }}>
            {/* Log Files List */}
            <div style={{
              background: colors.bgCard,
              borderRadius: '12px',
              border: '1px solid #333',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '16px',
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FolderOpen size={16} style={{ color: '#888' }} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#e5e5e5' }}>
                  Log Files
                </span>
              </div>
              
              <div style={{ height: '520px', overflow: 'auto' }}>
                {Object.entries(groupedLogFiles).map(([directory, files]) => (
                  <div key={directory}>
                    <div
                      onClick={() => toggleDirectory(directory)}
                      style={{
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        borderBottom: `1px solid ${colors.border}`,
                        background: '#333'
                      }}
                    >
                      {expandedDirectories.has(directory) ? 
                        <ChevronDown size={14} style={{ color: '#888' }} /> : 
                        <ChevronRight size={14} style={{ color: '#888' }} />
                      }
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#e5e5e5' }}>
                        {directory} ({files.length})
                      </span>
                    </div>
                    
                    {expandedDirectories.has(directory) && files.map(file => (
                      <div
                        key={file.path}
                        onClick={() => selectLogFile(file)}
                        style={{
                          padding: '12px 16px 12px 36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.border}`,
                          background: selectedLogFile?.path === file.path ? '#404040' : 'transparent',
                          transition: 'background 0.2s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <File size={14} style={{ color: getLogTypeColor(file.type) }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', color: '#e5e5e5', fontWeight: '500' }}>
                              {file.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              {formatBytes(file.size)} • {new Date(file.modified).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Log Content Viewer */}
            <div style={{
              background: colors.bgCard,
              borderRadius: '12px',
              border: '1px solid #333',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {selectedLogFile ? (
                <>
                  {/* Log Viewer Header */}
                  <div style={{
                    padding: '16px',
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
                        {selectedLogFile.name}
                      </h4>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        {selectedLogFile.directory} • {formatBytes(selectedLogFile.size)}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        color: '#888',
                        fontSize: '12px'
                      }}>
                        <input
                          type="checkbox"
                          checked={logAutoRefresh}
                          onChange={(e) => setLogAutoRefresh(e.target.checked)}
                          style={{ accentColor: '#ef4444' }}
                        />
                        Auto-refresh
                      </label>
                      
                      <button
                        onClick={() => loadLogContent(selectedLogFile.path)}
                        style={{
                          padding: '6px 12px',
                          background: '#333',
                          border: '1px solid #444',
                          borderRadius: '6px',
                          color: '#e5e5e5',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        <RefreshCw size={12} />
                      </button>
                      
                      <button
                        onClick={() => downloadLogFile(selectedLogFile.path)}
                        style={{
                          padding: '6px 12px',
                          background: '#333',
                          border: '1px solid #444',
                          borderRadius: '6px',
                          color: '#e5e5e5',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        <Download size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Search Bar */}
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Search in log file..."
                        value={logSearchTerm}
                        onChange={(e) => setLogSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchLogContent()}
                        style={{
                          width: '100%',
                          padding: '8px 12px 8px 36px',
                          background: '#333',
                          border: '1px solid #444',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px'
                        }}
                      />
                      <Search size={16} style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#888'
                      }} />
                    </div>
                    
                    <button
                      onClick={searchLogContent}
                      disabled={!logSearchTerm.trim() || logLoading}
                      style={{
                        padding: '8px 16px',
                        background: logSearchTerm.trim() ? '#ef4444' : '#555',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: logSearchTerm.trim() ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Search
                    </button>
                    
                    {logViewMode === 'search' && (
                      <button
                        onClick={() => {
                          setLogViewMode('recent');
                          setLogSearchResults([]);
                          loadLogContent(selectedLogFile.path);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#333',
                          color: '#e5e5e5',
                          border: '1px solid #444',
                          borderRadius: '8px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Show Recent
                      </button>
                    )}
                  </div>

                  {/* Log Content */}
                  <div style={{
                    flex: 1,
                    overflow: 'auto',
                    background: '#1a1a1a',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    position: 'relative'
                  }}>
                    {logLoading && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#888'
                      }}>
                        <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Loading...
                      </div>
                    )}
                    
                    {!logLoading && logViewMode === 'recent' && logContent.map((line, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '4px 16px',
                          borderBottom: '1px solid #262626',
                          display: 'flex',
                          gap: '12px',
                          fontSize: '12px'
                        }}
                      >
                        <span style={{ 
                          color: '#666', 
                          minWidth: '50px',
                          textAlign: 'right',
                          userSelect: 'none'
                        }}>
                          {line.number}
                        </span>
                        <span style={{ 
                          color: line.content.includes('ERROR') ? '#ef4444' :
                                line.content.includes('WARN') ? '#f59e0b' :
                                line.content.includes('INFO') ? '#10b981' : '#e5e5e5',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }}>
                          {line.content}
                        </span>
                      </div>
                    ))}
                    
                    {!logLoading && logViewMode === 'search' && logSearchResults.map((result, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '4px 16px',
                          borderBottom: '1px solid #262626',
                          display: 'flex',
                          gap: '12px',
                          fontSize: '12px'
                        }}
                      >
                        <span style={{ 
                          color: '#666', 
                          minWidth: '50px',
                          textAlign: 'right',
                          userSelect: 'none'
                        }}>
                          {result.line_number}
                        </span>
                        <span style={{ 
                          color: '#e5e5e5',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {result.content.split(logSearchTerm).map((part, i) => (
                            i === 0 ? part : (
                              <React.Fragment key={i}>
                                <span style={{ background: '#f59e0b', color: '#000', padding: '0 2px' }}>
                                  {logSearchTerm}
                                </span>
                                {part}
                              </React.Fragment>
                            )
                          ))}
                        </span>
                      </div>
                    ))}
                    
                    {!logLoading && logViewMode === 'search' && logSearchResults.length === 0 && logSearchTerm && (
                      <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#888'
                      }}>
                        No matches found for "{logSearchTerm}"
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#888',
                  fontSize: '14px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <Terminal size={48} style={{ color: '#333', marginBottom: '12px' }} />
                    <div>Select a log file to view its contents</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Add spinning animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};